import React, { useMemo, useState } from 'react';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import { processImageContentForSave, TiptapRichTextEditor } from '../DesignSystems/New/TiptapRichTextEditor';
import { SharedIcon } from '../SharedIcon/SharedIcon';
import {
  SitrepSectionType,
  useUpdateSitrepSectionV2,
  isSitrepSectionConflictError,
  type SitrepSectionConflictDetails,
  useAddImageToSitrep,
  useDeleteSitrepImage,
  useSitrepSectionImages,
} from '../DisasterGPT/hooks/useSitrepsV2';
import Sentry from '../../instrument';
import SitrepSectionConflictModal from './SitrepSectionConflictModal';
import { useSelector } from 'react-redux';
import { selectGroupGuid } from 'slices/commonSelectors';

interface ReportsSectionDisplayProps {
  section: SitrepSectionType;
  sitrepId: string;
  sitrepWorkspaceId: string | null;
  onUpdated?: () => void;
  canEdit?: boolean;
}

const ReportsSectionDisplay: React.FC<ReportsSectionDisplayProps> = ({
  section,
  sitrepId,
  sitrepWorkspaceId,
  onUpdated,
  canEdit = false,
}) => {
  const [editingContent, setEditingContent] = useState(false);
  const [content, setContent] = useState('');
  // version_id the user started editing from; used as the optimistic
  // concurrency token on save so the backend can detect collaborator edits.
  const [baseVersionId, setBaseVersionId] = useState<string | undefined>(
    undefined
  );
  const [conflict, setConflict] = useState<SitrepSectionConflictDetails | null>(
    null
  );

  const updateSectionMutation = useUpdateSitrepSectionV2();

  // The image will either be found under the sitrep workspace (for incident sitreps) or under the group (for group sitreps)
  const groupId = useSelector(selectGroupGuid);
  const imageOwnerId = sitrepWorkspaceId || groupId;

  const { mutateAsync: uploadSitrepImage } = useAddImageToSitrep(
    sitrepId,
    section.id,
    imageOwnerId
  );
  const { data: sitrepImages } = useSitrepSectionImages(section.id);

  const { mutateAsync: removeImageFromSitrep } = useDeleteSitrepImage(
    sitrepId,
    section.id,
    imageOwnerId
  );

  const handleDeleteImage = async (imageId: string) => {
    await removeImageFromSitrep(imageId);
  };

  // Memoize latestVersion to avoid recalculating on every render
  const latestVersion = useMemo(
    () => section.section_versions?.[0],
    [section.section_versions]
  );

  // Memoize sectionText derived from latestVersion
  const sectionText = useMemo(
    () => latestVersion?.sitrep_section_text || '',
    [latestVersion?.sitrep_section_text]
  );

  // Memoize sectionStatus derived from latestVersion
  const sectionStatus = useMemo(
    () => latestVersion?.status?.toLowerCase() || '',
    [latestVersion?.status]
  );

  // Check if section is processing (only active processing states, not completed states)
  // Memoize to avoid recalculating array includes on every render
  const isProcessing = useMemo(
    () => ['not started', 'fetching', 'uploading'].includes(sectionStatus),
    [sectionStatus]
  );

  const saveContent = async (
    overrideBaseVersionId?: string,
    overrideContent?: string
  ) => {
    const chosenContent = overrideContent ?? content;

    const contentWithFileImages = processImageContentForSave(chosenContent, sitrepImages || []);

    try {
      await updateSectionMutation.mutateAsync({
        sitrepId,
        sectionId: section.id,
        baseVersionId: overrideBaseVersionId ?? baseVersionId,
        updatedFields: { sectionText: contentWithFileImages },
      });
      setContent(sectionText);
      setEditingContent(false);
      setBaseVersionId(undefined);
      setConflict(null);
      onUpdated?.();
    } catch (error) {
      if (isSitrepSectionConflictError(error)) {
        // Surface the conflict in the modal so the user can choose
        // whether to keep the other user's version or override.
        setConflict(error.conflict);
        return;
      }
      Sentry.captureException(error);
    }
  };

  const handleSaveContent = () => saveContent();

  const handleOverrideConflict = (
    latestVersionId: string,
    editedContent: string
  ) => {
    setConflict(null);
    // Resave with the latest version id so the backend's concurrency
    // check passes and our edits replace the collaborator's content.
    // The edited content comes from the modal's editor, which may include
    // further edits the user made after the conflict was raised.
    saveContent(latestVersionId, editedContent);
  };

  const handleKeepExisting = () => {
    // Discard our edits, accept the other user's version, and refresh.
    setConflict(null);
    setEditingContent(false);
    setBaseVersionId(undefined);
    onUpdated?.();
  };

  const handleStartEditContent = () => {
    if (!canEdit || !enableEditing) {
      return;
    }

    setContent(sectionText);
    setBaseVersionId(latestVersion?.version_id);
    setEditingContent(true);
  };

  const handleCancelContent = () => {
    setEditingContent(false);
    setBaseVersionId(undefined);
  };

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  const isLoading = updateSectionMutation.isPending;

  const enableEditing = !(section.type === 'ai' && isProcessing);
  const canInteract = canEdit && enableEditing;

  const isEditing = editingContent && canEdit;

  const handleImageUploadClick = (files: File[]) => {
    uploadSitrepImage({ files: Array.from(files) });
  };

  return (
    <div className="reports-section">
      {/* Processing indicator */}
      {isProcessing && (
        <div className="d-flex align-items-center gap-2 mb-2">
          <SharedIcon iconName="sync" size="sm" classes="spinning" />
          <em className="text-muted small">Processing…</em>
        </div>
      )}

      <div className="reports-section__content">
        {isEditing ? (
          <div className="reports-section__content-edit">
            <TiptapRichTextEditor
              value={content}
              onChange={(html) => setContent(html)}
              placeholder="Enter section content..."
              disabled={isLoading}
              minHeight="150px"
              autoFocus
              className="w-100"
              onClickUploadImage={handleImageUploadClick}
              onClickDeleteImage={handleDeleteImage}
              imageLibrary={sitrepImages || []}
              imageLibraryName={`Section Image Library: ${section.name}`}
              normalizeMarkdown
            />
            <div className="reports-section__edit-actions">
              <StylishNewButton
                className="button--primary button--sml"
                onClick={handleSaveContent}
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save'}
              </StylishNewButton>
              <StylishNewButton
                className="button--secondary button--sml"
                onClick={handleCancelContent}
              >
                Cancel
              </StylishNewButton>
            </div>
          </div>
        ) : canInteract ? (
          <div
            className="reports-section__text reports-section__editable"
            onClick={handleStartEditContent}
            onKeyDown={(e) => handleKeyDown(e, handleStartEditContent)}
            role="button"
            tabIndex={0}
            title="Click to edit content"
          >
            {sectionText ? (
              <TiptapRichTextEditor
                value={sectionText}
                readOnly
                showToolbar={false}
                className="reports-section__html-content"
                imageLibrary={sitrepImages || []}
                normalizeMarkdown
              />
            ) : (
              <em className="text-muted">Click to add content</em>
            )}
          </div>
        ) : enableEditing ? (
          <div className="reports-section__text">
            {sectionText ? (
              <TiptapRichTextEditor
                value={sectionText}
                readOnly
                showToolbar={false}
                className="reports-section__html-content"
                imageLibrary={sitrepImages || []}
                normalizeMarkdown
              />
            ) : (
              <em className="text-muted">No content added yet</em>
            )}
          </div>
        ) : (
          <div
            className="reports-section__text reports-section__disabled"
            title="PRATUS AI section is processing - editing disabled"
          >
            {sectionText ? (
              <TiptapRichTextEditor
                value={sectionText}
                readOnly
                showToolbar={false}
                className="reports-section__html-content"
                imageLibrary={sitrepImages || []}
                normalizeMarkdown
              />
            ) : section.type === 'ai' && isProcessing ? (
              <em className="text-muted">
                <SharedIcon
                  iconName="auto_awesome"
                  size="sm"
                  classes="me-1 spinning"
                />
                PRATUS AI is generating content...
              </em>
            ) : (
              <em className="text-muted">No content added yet</em>
            )}
          </div>
        )}
      </div>
      <SitrepSectionConflictModal
        show={!!conflict}
        conflict={conflict}
        draftContent={content}
        onKeepExisting={handleKeepExisting}
        onOverride={handleOverrideConflict}
        isOverriding={isLoading}
      />
    </div>
  );
};

export default ReportsSectionDisplay;
